import React, { useContext, useRef, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  CircularProgress,
  Divider,
  Grid,
  IconButton,
  Tooltip,
  Typography,
} from "@mui/material";
import DownloadIcon from "@mui/icons-material/Download";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import DeleteIcon from "@mui/icons-material/Delete";
import LanguageIcon from "@mui/icons-material/Language";
import StorageIcon from "@mui/icons-material/Storage";
import client from "@/services/apiClient";
import { useTextDomainContext } from "@/app/providers";
import { usePageTitle } from "@/hooks";
import { enqueueSnackbar } from "notistack";

interface TranslationTargetConfig {
  id: "www" | "app";
  name: string;
  description: string;
  downloadUrl: string;
  icon: "web" | "app";
}

const formatFileSize = (bytes: number): string => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
};

interface TargetSectionProps {
  target: TranslationTargetConfig;
  graphqlUrl: string;
}

const TargetTranslationCard: React.FC<TargetSectionProps> = ({
  target,
  graphqlUrl,
}) => {
  const { TextDomainContext } = useTextDomainContext();
  const { gettext } = useContext(TextDomainContext);

  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [loading, setLoading] = useState(false);
  const [statusResult, setStatusResult] = useState<{
    type: "success" | "error";
    message: string;
  } | null>(null);

  const handleDownload = () => {
    window.open(target.downloadUrl, "_blank");
  };

  const validateAndSetFile = (file: File) => {
    if (!file.name.toLowerCase().endsWith(".zip")) {
      const errMsg = gettext(
        "Please select a .zip archive containing PO files.",
      );
      setStatusResult({ type: "error", message: errMsg });
      enqueueSnackbar(errMsg, { variant: "error" });
      return;
    }
    setSelectedFile(file);
    setStatusResult(null);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      validateAndSetFile(e.dataTransfer.files[0]);
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      validateAndSetFile(e.target.files[0]);
    }
  };

  const handleRemoveFile = (e: React.MouseEvent) => {
    e.stopPropagation();
    setSelectedFile(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleUpload = async () => {
    if (!selectedFile) {
      enqueueSnackbar(gettext("Please select a ZIP file to upload."), {
        variant: "warning",
      });
      return;
    }

    setLoading(true);
    setStatusResult(null);

    const formData = new FormData();
    const query = `mutation UploadTranslationZip($target: String) {
      uploadTranslationZip(target: $target) {
        success
        message
      }
    }`;
    formData.append("query", query);
    formData.append("variables", JSON.stringify({ target: target.id }));
    formData.append("file", selectedFile);

    try {
      const response = await client.post(graphqlUrl, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
        timeout: 180000,
      });

      const result = response?.data?.data?.uploadTranslationZip;
      if (result?.success) {
        const msg =
          result?.message?.[0] ||
          gettext(
            "Translation ZIP uploaded and synced to Git repository successfully!",
          );
        setStatusResult({ type: "success", message: msg });
        enqueueSnackbar(msg, { variant: "success" });
        setSelectedFile(null);
        if (fileInputRef.current) {
          fileInputRef.current.value = "";
        }
      } else {
        const errMsg =
          result?.message?.[0] ||
          response?.data?.errors?.[0]?.message ||
          gettext("Failed to process translation upload.");
        setStatusResult({ type: "error", message: errMsg });
        enqueueSnackbar(errMsg, { variant: "error" });
      }
    } catch (err: any) {
      const errMsg =
        err?.response?.data?.errors?.[0]?.message ||
        err?.message ||
        gettext("Network or server error while uploading translation file.");
      setStatusResult({ type: "error", message: errMsg });
      enqueueSnackbar(errMsg, { variant: "error" });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card
      sx={{
        height: "100%",
        display: "flex",
        flexDirection: "column",
        borderRadius: 3,
        boxShadow: 2,
        transition: "box-shadow 0.2s ease-in-out",
        "&:hover": {
          boxShadow: 4,
        },
      }}
    >
      <CardHeader
        avatar={
          target.icon === "web" ? (
            <LanguageIcon color="primary" fontSize="large" />
          ) : (
            <StorageIcon color="primary" fontSize="large" />
          )
        }
        title={
          <Typography variant="h6" sx={{ fontWeight: 700 }}>
            {target.name}
          </Typography>
        }
        subheader={
          <Typography variant="body2" color="textSecondary" sx={{ mt: 0.5 }}>
            {target.description}
          </Typography>
        }
      />

      <Divider />

      <CardContent
        sx={{
          flexGrow: 1,
          display: "flex",
          flexDirection: "column",
          gap: 3,
          p: 3,
        }}
      >
        {/* Step 1: Download Section */}
        <Box>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 1,
            }}
          >
            <Typography
              variant="subtitle2"
              sx={{
                fontWeight: 700,
                textTransform: "uppercase",
                color: "text.secondary",
              }}
            >
              {gettext("1. Download Current Translations")}
            </Typography>
          </Box>
          <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
            {gettext(
              "Download the latest translation archive (.zip) containing existing PO and POT files to edit translations using Poedit or your preferred editor.",
            )}
          </Typography>
          <Button
            variant="outlined"
            startIcon={<DownloadIcon />}
            onClick={handleDownload}
            sx={{ borderRadius: 2, textTransform: "none", fontWeight: 600 }}
          >
            {gettext(`Download ${target.name} Translation ZIP`)}
          </Button>
        </Box>

        <Divider />

        {/* Step 2: Upload Section */}
        <Box sx={{ flexGrow: 1, display: "flex", flexDirection: "column" }}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 1,
            }}
          >
            <Typography
              variant="subtitle2"
              sx={{
                fontWeight: 700,
                textTransform: "uppercase",
                color: "text.secondary",
              }}
            >
              {gettext("2. Upload & Sync PO Archive")}
            </Typography>
          </Box>
          <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
            {gettext(
              "Upload a ZIP file containing the modified PO files. The system will pull the latest code for the active environment branch (dev, stage, or production), replace PO files in languages/LC_MESSAGES, and push the changes directly to that branch.",
            )}
          </Typography>

          {/* Dropzone area */}
          <Box
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            onClick={() => !loading && fileInputRef.current?.click()}
            sx={{
              p: 3,
              borderRadius: 2,
              border: "2px dashed",
              borderColor: isDragging
                ? "primary.main"
                : selectedFile
                  ? "success.main"
                  : "divider",
              backgroundColor: isDragging
                ? "action.hover"
                : selectedFile
                  ? "action.selected"
                  : "background.default",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              textAlign: "center",
              cursor: loading ? "not-allowed" : "pointer",
              transition: "all 0.2s ease-in-out",
              mb: 2,
              "&:hover": {
                borderColor: loading ? "divider" : "primary.main",
                backgroundColor: loading
                  ? "background.default"
                  : "action.hover",
              },
            }}
          >
            <input
              type="file"
              ref={fileInputRef}
              accept=".zip,application/zip"
              style={{ display: "none" }}
              onChange={handleFileChange}
              disabled={loading}
            />

            {selectedFile ? (
              <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                <CheckCircleIcon color="success" sx={{ fontSize: 32 }} />
                <Box sx={{ textAlign: "left" }}>
                  <Typography variant="body1" sx={{ fontWeight: 600 }}>
                    {selectedFile.name}
                  </Typography>
                  <Typography variant="caption" color="textSecondary">
                    {formatFileSize(selectedFile.size)}
                  </Typography>
                </Box>
                {!loading && (
                  <Tooltip title={gettext("Remove file")}>
                    <IconButton
                      size="small"
                      onClick={handleRemoveFile}
                      sx={{ ml: 1 }}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                )}
              </Box>
            ) : (
              <>
                <CloudUploadIcon
                  sx={{ fontSize: 40, color: "text.secondary", mb: 1 }}
                />
                <Typography
                  variant="body2"
                  sx={{ fontWeight: 600, color: "text.primary" }}
                >
                  {gettext(
                    "Drag and drop your translation .zip file here, or click to browse",
                  )}
                </Typography>
                <Typography
                  variant="caption"
                  color="textSecondary"
                  sx={{ mt: 0.5 }}
                >
                  {gettext("Supported format: .zip containing .po files")}
                </Typography>
              </>
            )}
          </Box>

          {/* Status Message */}
          {statusResult && (
            <Alert severity={statusResult.type} sx={{ mb: 2, borderRadius: 2 }}>
              {statusResult.message}
            </Alert>
          )}

          {/* Action buttons */}
          <Box sx={{ mt: "auto", display: "flex", justifyContent: "flex-end" }}>
            <Button
              variant="contained"
              color="primary"
              disabled={!selectedFile || loading}
              onClick={handleUpload}
              startIcon={
                loading ? (
                  <CircularProgress size={18} color="inherit" />
                ) : (
                  <CloudUploadIcon />
                )
              }
              sx={{
                borderRadius: 2,
                textTransform: "none",
                fontWeight: 600,
                px: 3,
                py: 1,
              }}
            >
              {loading
                ? gettext("Processing & Pushing to Git...")
                : gettext(`Upload to ${target.name}`)}
            </Button>
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
};

export const Translation: React.FC = () => {
  const { VITE_API_URL_GRAPHQL, VITE_URL } = import.meta.env;
  const { TextDomainContext } = useTextDomainContext();
  const { gettext } = useContext(TextDomainContext);

  usePageTitle(gettext("Translation Management"));

  const targets: TranslationTargetConfig[] = [
    {
      id: "www",
      name: gettext("WWW (Website)"),
      description: gettext(
        "Manage translations for the www.fabeke.com website repository.",
      ),
      downloadUrl: "https://www.fabeke.com/data/gettext/translation.zip",
      icon: "web",
    },
    {
      id: "app",
      name: gettext("App (Web Application)"),
      description: gettext(
        "Manage translations for the app.fabeke.com web application repository.",
      ),
      downloadUrl: `${VITE_URL || ""}/data/gettext/translation.zip`,
      icon: "app",
    },
  ];

  return (
    <Box sx={{ p: { xs: 2, md: 4 } }}>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" sx={{ fontWeight: 800, mb: 1 }}>
          {gettext("Translation Management")}
        </Typography>
        <Typography variant="body1" color="textSecondary">
          {gettext(
            "Download translation PO files for offline editing or upload a ZIP archive of updated PO files to automatically sync them with the active Git environment branch.",
          )}
        </Typography>
      </Box>

      {/* Target Sections Grid */}
      <Grid container spacing={3}>
        {targets.map((target) => (
          <Grid size={{ xs: 12, md: 6 }} key={target.id}>
            <TargetTranslationCard
              target={target}
              graphqlUrl={VITE_API_URL_GRAPHQL}
            />
          </Grid>
        ))}
      </Grid>
    </Box>
  );
};
